import React from 'react';
import { cn } from '../../../lib/utils';
import { StatusPill } from '@/components/app';
import { UserSummary } from '../../../api/duplicates';
import { getErrorCode, getErrorMessage } from '@/lib/errors';

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function tgHandle(u: Pick<UserSummary, 'telegramUsername' | 'telegramId'>): string {
  if (u.telegramUsername) return `@${u.telegramUsername}`;
  if (u.telegramId) return `id ${u.telegramId}`;
  return '—';
}

export function userTitle(u: UserSummary): string {
  return u.username || u.email || u.firstName || u.id.slice(0, 8);
}

/** Премиум активен, если подписка отмечена и срок ещё не истёк (или срок не указан). */
function premiumActive(u: UserSummary): boolean {
  if (!u.isSubscribed) return false;
  if (!u.subscribedEnd) return true;
  return new Date(u.subscribedEnd).getTime() > Date.now();
}

/** Русский текст ошибки по `code` (сырой текст сервера не показываем) — см. lib/errors.ts. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  return getErrorMessage(err, fallback);
}

export { getErrorCode };

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5 text-sm">
      <span className="shrink-0 text-text-2">{label}</span>
      <span className="break-all text-right font-medium text-text">{children}</span>
    </div>
  );
}

interface UserSummaryCardProps {
  user: UserSummary;
  role: string;
  /** Подсветка роли: дубль (Telegram) — нейтральная рамка, основной — акцентная */
  tone?: 'duplicate' | 'main' | 'neutral';
  footer?: React.ReactNode;
}

export function UserSummaryCard({ user, role, tone = 'neutral', footer }: UserSummaryCardProps) {
  const premium = premiumActive(user);
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-2 rounded-xl border p-4',
        tone === 'main' ? 'border-accent bg-accent-soft' : tone === 'duplicate' ? 'border-border bg-surface-alt' : 'border-border-soft bg-surface',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className={cn('text-[11px] font-semibold uppercase tracking-[0.05em]', tone === 'main' ? 'text-accent-text' : 'text-text-2')}>
            {role}
          </p>
          <p className="truncate font-semibold text-text">{userTitle(user)}</p>
          <p className="break-all font-mono text-[11px] text-text-3">{user.id}</p>
        </div>
        <div className="flex flex-wrap justify-end gap-1">
          {premium && <StatusPill size="sm" tone="info">Премиум</StatusPill>}
          {user.hasPayments && <StatusPill size="sm" tone="pos">Есть оплаты</StatusPill>}
          {user.mergedIntoUserId && <StatusPill size="sm" tone="neutral">Склеен</StatusPill>}
          {user.isDeleted && <StatusPill size="sm" tone="neg">Удалён</StatusPill>}
        </div>
      </div>

      <div className="border-t border-border-soft pt-2">
        <Field label="Username">{user.username || '—'}</Field>
        <Field label="Email">{user.email || '—'}</Field>
        <Field label="Телефон">{user.phone || '—'}</Field>
        <Field label="Telegram">{tgHandle(user)}</Field>
        <Field label="Создан">{formatDate(user.createdAt)}</Field>
        <Field label="Последний вход">{formatDate(user.lastLogin)}</Field>
        <Field label="Премиум до">{user.isSubscribed ? formatDate(user.subscribedEnd) : '—'}</Field>
        <Field label="Оплаты">{user.hasPayments ? 'есть' : 'нет'}</Field>
      </div>

      {footer && <div className="pt-1">{footer}</div>}
    </div>
  );
}
